import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Search } from "lucide-react";
import { CameraStatus } from "@/cameras/camera-status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { Ago, EmptyBlock, Meter, Sparkline } from "../../components/primitives";
import { DASH, formatCount, formatMs, formatPercent } from "../../format";
import { NEUTRAL } from "../../palette";
import { invalidTone, searchText } from "../pipeline/helpers";
import { BranchFlags, Chip, InlineStat, ProblemCount } from "../pipeline/shared";
import { CameraDetails } from "./camera-details";
import { CAMERA_FILTERS, matchesFilter, type CameraFilter, type CameraProblem, type CameraRow } from "./model";

/** Camera list of the tab: filter + search, a table from md up, cards below; every row expands to details. */
export function CameraList({ rows }: { rows: CameraRow[] }) {
  const { t } = useLocalization();
  const [filter, setFilter] = useState<CameraFilter>("all");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const counts = useMemo(
    () => Object.fromEntries(CAMERA_FILTERS.map((f) => [f, rows.filter((r) => matchesFilter(r, f)).length])) as Record<CameraFilter, number>,
    [rows],
  );
  const visible = useMemo(() => {
    const needle = searchText(query.trim());
    return rows.filter((r) => matchesFilter(r, filter) && (!needle || r.search.includes(needle)));
  }, [rows, filter, query]);

  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={filter}
          onValueChange={(value) => value && setFilter(value as CameraFilter)}
          aria-label={t("monitoring.cameras.filter.label")}
          className="flex-wrap justify-start gap-0.5"
        >
          {CAMERA_FILTERS.map((f) => (
            <ToggleGroupItem key={f} value={f} className="h-8 gap-1 px-2.5 text-xs">
              {t(`monitoring.cameras.filter.${f}`)}
              <span
                className={cn(
                  "tabular-nums text-muted-foreground",
                  f === "problems" && counts.problems > 0 && "font-semibold text-red-400",
                )}
              >
                {counts[f]}
              </span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <div className="relative min-w-[12rem] flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("monitoring.cameras.search")}
            aria-label={t("monitoring.cameras.search")}
            className="h-8 pl-8 text-xs md:text-xs"
          />
        </div>
      </div>

      {visible.length === 0 ? (
        <EmptyBlock>{rows.length ? t("monitoring.cameras.list.empty") : t("monitoring.cameras.list.none")}</EmptyBlock>
      ) : (
        <>
          <div className="hidden md:block">
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="h-8 min-w-[10rem]">{t("monitoring.cameras.col.camera")}</TableHead>
                  <TableHead className="h-8 min-w-[8rem]">{t("monitoring.col.branch")}</TableHead>
                  <TableHead className="h-8">{t("monitoring.cameras.col.seen")}</TableHead>
                  <TableHead className="h-8">{t("monitoring.cameras.col.captures")}</TableHead>
                  <TableHead className="hidden h-8 min-[1140px]:table-cell">{t("monitoring.cameras.col.invalid")}</TableHead>
                  <TableHead className="hidden h-8 xl:table-cell">{t("monitoring.col.latency")}</TableHead>
                  <TableHead className="hidden h-8 min-[1440px]:table-cell">{t("monitoring.cameras.col.instance")}</TableHead>
                  <TableHead className="h-8 w-8">
                    <span className="sr-only">{t("monitoring.cameras.details")}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((row) => {
                  const open = expanded.has(row.camera.id);
                  return (
                    <Fragment key={row.camera.id}>
                      <TableRow
                        className={cn(
                          "align-top",
                          row.severity === "critical" && "bg-red-500/[0.04]",
                          open && "border-b-0",
                        )}
                      >
                        <TableCell className="py-2.5 align-top">
                          <CameraName row={row} />
                        </TableCell>
                        <TableCell className="py-2.5 align-top">
                          <BranchCell row={row} />
                        </TableCell>
                        <TableCell className="py-2.5 align-top">
                          <SeenCell row={row} />
                        </TableCell>
                        <TableCell className="py-2.5 align-top">
                          <CapturesCell row={row} />
                        </TableCell>
                        <TableCell className="hidden py-2.5 align-top min-[1140px]:table-cell">
                          <InvalidCell row={row} />
                        </TableCell>
                        <TableCell className="hidden py-2.5 align-top xl:table-cell">
                          <LatencyCell row={row} />
                        </TableCell>
                        <TableCell className="hidden py-2.5 align-top min-[1440px]:table-cell">
                          <InstanceCell row={row} />
                        </TableCell>
                        <TableCell className="py-2 align-top">
                          <ExpandButton open={open} onClick={() => toggle(row.camera.id)} />
                        </TableCell>
                      </TableRow>
                      {open && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={8} className="bg-muted/20 px-3 pb-4 pt-2">
                            <CameraDetails row={row} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <ul className="flex flex-col gap-2 md:hidden">
            {visible.map((row) => (
              <CameraCard key={row.camera.id} row={row} open={expanded.has(row.camera.id)} onToggle={() => toggle(row.camera.id)} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function ExpandButton({ open, onClick }: { open: boolean; onClick: () => void }) {
  const { t } = useLocalization();
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 w-7 p-0"
      onClick={onClick}
      aria-expanded={open}
      aria-label={open ? t("monitoring.cameras.hideDetails") : t("monitoring.cameras.details")}
      title={open ? t("monitoring.cameras.hideDetails") : t("monitoring.cameras.details")}
    >
      <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
    </Button>
  );
}

function CameraName({ row }: { row: CameraRow }) {
  const { camera } = row;
  return (
    <div className="flex min-w-0 flex-col gap-1">
      {/* an unassigned camera being offline is not a problem: keep its state calm */}
      <CameraStatus
        connectivity={camera.connectivity}
        showLastSeen={false}
        className={camera.assigned ? undefined : "opacity-60 grayscale"}
      />
      <Link to={`/cameras/${camera.id}`} className="break-words text-sm font-medium leading-snug hover:underline">
        {camera.name || camera.id}
      </Link>
      {camera.label && camera.label !== camera.name && (
        <span className="break-words text-[11px] leading-tight text-muted-foreground">{camera.label}</span>
      )}
      <ProblemChips problems={row.problems} />
    </div>
  );
}

function ProblemChips({ problems }: { problems: CameraProblem[] }) {
  // offline shows as the red status pill already
  const shown = problems.filter((p) => p.code !== "offline");
  if (!shown.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((problem) => (
        <ProblemChip key={problem.code} problem={problem} />
      ))}
    </div>
  );
}

function ProblemChip({ problem }: { problem: CameraProblem }) {
  const { t, tp } = useLocalization();
  const tone = problem.severity === "critical" ? "nok" : "warn";
  switch (problem.code) {
    case "stuck":
      return (
        <Chip tone={tone} title={t("monitoring.cameras.problem.stuckHint")}>
          {tp("monitoring.cameras.problem.stuck", problem.count)} ·{" "}
          {problem.noPhoto === problem.count
            ? t("monitoring.cameras.problem.stuckNoPhoto")
            : problem.noPhoto === 0
              ? t("monitoring.cameras.problem.stuckEvaluation")
              : tp("monitoring.cameras.problem.stuckMixed", problem.noPhoto)}
        </Chip>
      );
    case "uncaptured":
      return (
        <Chip tone={tone} title={problem.manual ? t("monitoring.cameras.flag.manualTriggerHint") : t("monitoring.cameras.problem.uncapturedHint")}>
          {tp("monitoring.cameras.problem.uncaptured", problem.count)}
        </Chip>
      );
    case "triggerFailures":
      return (
        <Chip tone={tone} title={problem.error ?? undefined}>
          {tp("monitoring.cameras.problem.triggerFailures", problem.count)}
          {problem.lastFailed && ` · ${t("monitoring.cameras.problem.lastFailed")}`}
        </Chip>
      );
    case "uploadRejections":
      return (
        <Chip tone={tone} title={problem.error ?? undefined}>
          {tp("monitoring.cameras.problem.uploadRejections", problem.count)}
          {problem.lastRefused && ` · ${t("monitoring.cameras.problem.lastFailed")}`}
        </Chip>
      );
    case "restartPending":
      return (
        <Chip
          tone={tone}
          title={[
            problem.camera && t("monitoring.cameras.detail.restartCamera"),
            problem.service && t("monitoring.cameras.detail.restartService"),
          ]
            .filter(Boolean)
            .join(", ")}
        >
          {t("monitoring.cameras.problem.restartPending")}
        </Chip>
      );
    case "invalidShare":
      return <Chip tone={tone}>{t("monitoring.cameras.problem.invalidShare", { share: formatPercent(problem.share) })}</Chip>;
    default:
      return null;
  }
}

function BranchCell({ row }: { row: CameraRow }) {
  const { t } = useLocalization();
  const { camera } = row;
  if (!camera.assigned) return <span className="text-muted-foreground">{t("monitoring.cameras.unassigned")}</span>;
  return (
    <div className="min-w-0">
      <div className="break-words text-sm leading-snug">{camera.branch?.name || camera.branch_id || t("monitoring.unknownBranch")}</div>
      <BranchFlags acquisition={camera.branch?.enable_acquisition} bbTrigger={camera.branch?.enable_bb_trigger} />
    </div>
  );
}

function SeenCell({ row }: { row: CameraRow }) {
  const { t } = useLocalization();
  const { camera } = row;
  return (
    <div className="flex flex-col gap-0.5">
      <InlineStat label={t("monitoring.cameras.seen.thingsboard")}>
        <Ago ts={camera.connectivity?.last_activity_time} />
      </InlineStat>
      <InlineStat label={t("monitoring.cameras.seen.heartbeat")} title={t("monitoring.cameras.seen.heartbeatHint")}>
        <Ago ts={camera.activity?.last_flags_poll_at} />
      </InlineStat>
      <InlineStat label={t("monitoring.cameras.seen.lastCapture")}>
        <Ago ts={camera.stats?.last_capture_at} />
      </InlineStat>
    </div>
  );
}

function CapturesCell({ row }: { row: CameraRow }) {
  const { t, tp } = useLocalization();
  const stats = row.camera.stats;
  if (!stats) return <span className="text-muted-foreground">{DASH}</span>;
  return (
    <div className="flex min-w-[7rem] flex-col gap-1">
      <div className="flex flex-wrap items-baseline gap-x-1.5">
        <span className="text-sm font-semibold tabular-nums">{formatCount(stats.captured_24h)}</span>
        <span className="text-[11px] text-muted-foreground">{tp("monitoring.cameras.ofOrders", stats.orders_24h)}</span>
      </div>
      <Sparkline values={stats.series} bars color={NEUTRAL.strong} height={18} className="w-28" />
      <div className="flex flex-wrap gap-x-2">
        <InlineStat label={t("monitoring.cameras.stuckShort")}>
          <ProblemCount value={stats.missing_24h} tone={(stats.missing_no_photo_24h ?? stats.missing_24h) > 0 ? "nok" : "warn"} />
        </InlineStat>
        <InlineStat label={t("monitoring.cameras.uncapturedShort")} title={t("monitoring.cameras.problem.uncapturedHint")}>
          <ProblemCount value={stats.uncaptured} tone={row.camera.branch?.enable_bb_trigger === false ? "warn" : "nok"} />
        </InlineStat>
      </div>
    </div>
  );
}

function InvalidCell({ row }: { row: CameraRow }) {
  const share = row.camera.stats?.invalid_share_24h;
  if (share === null || share === undefined) return <span className="text-muted-foreground">{DASH}</span>;
  const tone = invalidTone(share);
  return (
    <div className="flex w-20 flex-col gap-1">
      <span className={cn("tabular-nums", tone === "nok" ? "font-semibold text-red-400" : tone === "warn" ? "text-amber-300" : undefined)}>
        {formatPercent(share)}
      </span>
      <Meter value={share} tone={tone} />
    </div>
  );
}

function LatencyCell({ row }: { row: CameraRow }) {
  const { t } = useLocalization();
  const stats = row.camera.stats;
  if (!stats) return <span className="text-muted-foreground">{DASH}</span>;
  return (
    <div className="flex min-w-[8.5rem] flex-col gap-0.5">
      <InlineStat label={t("monitoring.cameras.latency.upload")}>
        {formatMs(stats.upload_delay?.p50)} / {formatMs(stats.upload_delay?.p95)}
      </InlineStat>
      <InlineStat label={t("monitoring.cameras.latency.verdict")}>{formatMs(stats.capture_to_verdict?.p95)}</InlineStat>
    </div>
  );
}

function InstanceCell({ row }: { row: CameraRow }) {
  const { t } = useLocalization();
  const activity = row.camera.activity ?? {};
  return (
    <div className="flex min-w-[10rem] flex-col gap-0.5">
      <InlineStat label={t("monitoring.cameras.instance.trigger")} title={activity.last_trigger_error ?? undefined}>
        {activity.last_trigger_at ? (
          <>
            <span className={activity.last_trigger_ok === false ? "font-semibold text-red-400" : "text-muted-foreground"}>
              {activity.last_trigger_ok === false ? t("monitoring.cameras.instance.failed") : t("monitoring.verdict.ok")}
            </span>
            {typeof activity.last_trigger_ms === "number" && <span className="text-muted-foreground"> · {formatMs(activity.last_trigger_ms)}</span>}{" "}
            · <Ago ts={activity.last_trigger_at} />
          </>
        ) : (
          <span className="text-muted-foreground">{DASH}</span>
        )}
      </InlineStat>
      <InlineStat label={t("monitoring.cameras.instance.upload")} title={activity.last_upload_error ?? undefined}>
        {activity.last_upload_attempt_at ? (
          <>
            <span className={activity.last_upload_status !== 200 ? "font-semibold text-red-400" : "text-muted-foreground"}>
              {activity.last_upload_status ?? DASH}
            </span>{" "}
            · <Ago ts={activity.last_upload_attempt_at} />
          </>
        ) : (
          <span className="text-muted-foreground">{DASH}</span>
        )}
      </InlineStat>
      <div className="text-[11px] leading-snug text-muted-foreground">
        {t("monitoring.cameras.instance.counts", {
          triggers: formatCount(activity.triggers ?? 0),
          uploads: formatCount(activity.uploads ?? 0),
        })}{" "}
        {(activity.trigger_failures ?? 0) > 0 && (
          <span className="font-semibold text-red-400">
            · {t("monitoring.cameras.instance.failedCount", { count: formatCount(activity.trigger_failures ?? 0) })}{" "}
          </span>
        )}
        {(activity.upload_rejections ?? 0) > 0 && (
          <span className="font-semibold text-amber-300">
            · {t("monitoring.cameras.instance.refusedCount", { count: formatCount(activity.upload_rejections ?? 0) })}
          </span>
        )}
      </div>
    </div>
  );
}

function CameraCard({ row, open, onToggle }: { row: CameraRow; open: boolean; onToggle: () => void }) {
  const { t } = useLocalization();
  return (
    <li
      className={cn(
        "min-w-0 rounded-lg border p-3",
        row.severity === "critical" ? "border-red-500/40" : row.severity === "warning" ? "border-amber-500/35" : undefined,
      )}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <CameraName row={row} />
        </div>
        <ExpandButton open={open} onClick={onToggle} />
      </div>
      <div className="mt-2 border-t border-border/50 pt-2">
        <BranchCell row={row} />
      </div>
      <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 border-t border-border/50 pt-2 min-[420px]:grid-cols-2">
        <SeenCell row={row} />
        <CapturesCell row={row} />
        {row.camera.stats && (
          <>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] text-muted-foreground">{t("monitoring.cameras.col.invalid")}</span>
              <InvalidCell row={row} />
            </div>
            <LatencyCell row={row} />
          </>
        )}
        <div className="min-[420px]:col-span-2">
          <InstanceCell row={row} />
        </div>
      </div>
      {open && (
        <div className="mt-3 border-t border-border/50 pt-3">
          <CameraDetails row={row} />
        </div>
      )}
    </li>
  );
}
